import type { Ref } from 'vue'

/**
 * "Meanwhile: which job are you applying for?" — the dialog that asks for the
 * job posting while a CV is being built (LandingPostingDialog), shared by the
 * chat builder and the paste builder so both end on the same result: the CV,
 * already being compared with the posting.
 *
 * It opens only once the build has been running a moment, so a build refused
 * at once (the day's limit, a network error) never flashes it. It stays open
 * until answered or skipped; answered while the CV is still being written, it
 * waits for the CV and then gets out of the way.
 */
export const POSTING_DIALOG_DELAY_MS = 700

export interface PostingDialogOptions {
  isBuilding: Ref<boolean>
  /** Takes the answer, a posting or null for none; false when the text is too short to be an ad. */
  accept: (posting: string | null) => boolean
  /** The CV is there and the dialog is out of the way: show the result. */
  onDone: () => void
}

export function usePostingDialog(options: PostingDialogOptions) {
  const { t } = useI18n()

  const isOpen = ref(false)
  const text = ref('')
  const error = ref<string | null>(null)
  /** Answered or skipped; the dialog only waits for the CV now. */
  const answered = ref(false)
  let timer: ReturnType<typeof setTimeout> | undefined

  function show() {
    text.value = ''
    error.value = null
    answered.value = false
    isOpen.value = true
    nextTick(() => document.getElementById('posting-dialog-text')?.focus())
  }

  /** The build has started and the posting is not known yet. */
  function startBuild() {
    clearTimeout(timer)
    timer = setTimeout(() => {
      if (options.isBuilding.value) show()
    }, POSTING_DIALOG_DELAY_MS)
  }

  /**
   * The build is over. `built`: a CV came back. `asks`: the posting was not
   * known when it started.
   */
  function endBuild(built: boolean, asks: boolean) {
    clearTimeout(timer)
    // The error is shown where the build was started: the dialog makes way for it.
    if (!built) {
      isOpen.value = false
      return
    }
    // Built before the delay was up: still asked, with the CV behind the dialog.
    if (asks && !isOpen.value) return show()
    // Still deciding on the posting: the CV waits behind the dialog.
    if (isOpen.value && !answered.value) return
    isOpen.value = false
    options.onDone()
  }

  /** The posting, or null to go on without one (skip, the close button, a click outside). */
  function answer(posting: string | null) {
    if (answered.value) return
    if (!options.accept(posting)) {
      error.value = t('cvChat.posting.tooShort')
      return
    }
    answered.value = true
    if (options.isBuilding.value) return
    isOpen.value = false
    options.onDone()
  }

  /** The field's value; a method, so the dialog that shows it does not write into its prop. */
  function setText(value: string) {
    text.value = value
    error.value = null
  }

  onBeforeUnmount(() => clearTimeout(timer))

  return { isOpen, text, error, answered, startBuild, endBuild, answer, setText }
}

export type PostingDialog = ReturnType<typeof usePostingDialog>
